/**
 * Sorting and filtering for the station-volumes table (spec §4c).
 *
 * Pure, and outside the component, because "which bin has the most cut" and
 * "which bin holds STA 1+240" are questions with arithmetic answers — they
 * are worth testing without a DOM, and the component is easier to read when
 * it only wires them up.
 */
import { parseStation } from '@/core/alignment';

import type { CrossfallSide, StationBin, StationSectionLine } from './types';

export type StationSortKey =
  | 'station'
  | 'cut'
  | 'fill'
  | 'net'
  | 'cumulative'
  | 'coverage'
  | 'endArea'
  | 'aea'
  | 'cells'
  | 'thickness'
  | 'crossfallL'
  | 'crossfallR';

export interface StationSort {
  key: StationSortKey;
  /** 1 ascending, -1 descending. */
  dir: 1 | -1;
}

/**
 * The figure each column sorts on. `null` means "this bin has no figure for
 * that column" — an AEA the worker withheld because the local radius is
 * smaller than the swath — and sorts last in both directions, because a
 * missing figure is not a small one.
 */
/**
 * The end area a row prints.
 *
 * With station lines this is the TRUE area where the section plane cuts, at
 * the bin's START station. Without them — a run made before they existed — it
 * is the bin's mean over the interval, which is a different quantity and is
 * labelled as one.
 *
 * One helper, used by both the cell and the sort, because a column that sorts
 * on a different number than it prints is a bug the reader cannot see.
 */
export function endAreaFor(
  b: StationBin,
  stations: readonly StationSectionLine[] | null | undefined,
): { cut: number; fill: number; atStation: boolean } {
  const line = stations?.[b.i];
  return line
    ? { cut: line.cutAreaM2, fill: line.fillAreaM2, atStation: true }
    : { cut: b.cutAreaM2, fill: b.fillAreaM2, atStation: false };
}

/**
 * The crossfall side a row prints: the bin's START station line, the same
 * pairing `endAreaFor` uses, so the two columns of one row describe one
 * section rather than two.
 *
 * `null` on a run with no check, on a side the run never configured, and on a
 * line the check could not resolve — three different absences that all print
 * the same em dash, because a reader can only act on "there is no figure".
 */
export function crossfallFor(
  b: StationBin,
  stations: readonly StationSectionLine[] | null | undefined,
  side: 'left' | 'right',
): CrossfallSide | null {
  return stations?.[b.i]?.crossfall?.[side] ?? null;
}

const sortValues = (
  stations: readonly StationSectionLine[] | null | undefined,
): Record<StationSortKey, (b: StationBin) => number | null> => ({
  station: (b) => b.staStartInternalM,
  cut: (b) => b.cutVolumeM3,
  fill: (b) => b.fillVolumeM3,
  net: (b) => b.netVolumeM3,
  cumulative: (b) => b.cumulativeNetM3,
  coverage: (b) => b.coveragePct,
  // The column prints `cut / fill`, so it sorts on the pair. Sorting on the
  // cut half alone put every fill-only bin — `0.00 / 9.23` — in a single
  // indistinguishable block at the top, and the reader has no way to tell
  // that the order they asked for is not the order they are looking at.
  endArea: (b) => {
    const a = endAreaFor(b, stations);
    return a.cut + a.fill;
  },
  aea: (b) => b.cutVolumeAeaM3,
  cells: (b) => b.cellCount,
  // `meanThicknessM` is `undefined` on a run with no expectation (the column
  // is not even shown then) and `null` on a bin that measured nothing within
  // one that has an expectation — both fold to `null` here, which the sort
  // above already treats as "sorts last, either direction": a bin with no
  // figure to show is not a zero-thickness bin (design doc §4.3).
  thickness: (b) => b.meanThicknessM ?? null,
  // Sorted on the ABSOLUTE difference: the question a reader asks of this
  // column is "where is the crossfall furthest out", and the sign is a
  // direction, not a magnitude. A side with no figure is `null`, which the
  // comparator above already sorts LAST in both directions.
  //
  // It ranks what the CELL PRINTS, including a side the fit judged UNCERTAIN
  // — whose delta `crossfall.py::summarise` deliberately leaves out of
  // `worstAbsDeltaPct`. So the top of this sort is not the run's worst
  // TRUSTED fit and must not be quoted as one; read the verdict in the cell
  // beside the figure. A sort that ranked on a number the column does not
  // show would be the worse bug: the reader could not see why the order is
  // what it is.
  crossfallL: (b) => {
    const d = crossfallFor(b, stations, 'left')?.deltaPct;
    return d == null ? null : Math.abs(d);
  },
  crossfallR: (b) => {
    const d = crossfallFor(b, stations, 'right')?.deltaPct;
    return d == null ? null : Math.abs(d);
  },
});

/** Station reads low-to-high; a column of figures is asked "which is biggest". */
const defaultDir = (key: StationSortKey): 1 | -1 => (key === 'station' ? 1 : -1);

export const DEFAULT_SORT: StationSort[] = [{ key: 'station', dir: 1 }];

export function isDefaultSort(sorts: StationSort[]): boolean {
  return sorts.length === 1 && sorts[0].key === 'station' && sorts[0].dir === 1;
}

/** Sorted copy. Ties fall through to the next sort, then to station order, so
 *  the same data always lands in the same order. */
export function sortBins(
  bins: StationBin[],
  sorts: StationSort[],
  stations?: readonly StationSectionLine[] | null,
): StationBin[] {
  const rules = sorts.length > 0 ? sorts : DEFAULT_SORT;
  const value = sortValues(stations);
  return [...bins].sort((a, b) => {
    for (const { key, dir } of rules) {
      const va = value[key](a);
      const vb = value[key](b);
      if (va == null || vb == null) {
        if (va == null && vb == null) continue;
        return va == null ? 1 : -1; // missing figures last, either direction
      }
      if (va !== vb) return (va - vb) * dir;
    }
    return a.i - b.i;
  });
}

/**
 * What a click on a column header does.
 *
 * A plain click sorts by that column alone: leaving an invisible secondary
 * sort behind is how a reader ends up unable to explain the order they are
 * looking at. Shift-click keeps the primary and works on the secondary, and
 * we stop at two — a three-deep sort is not something anyone holds in their
 * head while reading a quantity.
 */
export function nextSort(current: StationSort[], key: StationSortKey, shift: boolean): StationSort[] {
  const flip = (s: StationSort): StationSort => ({ key: s.key, dir: (s.dir * -1) as 1 | -1 });

  if (!shift) {
    const primary = current[0];
    return [primary?.key === key ? flip(primary) : { key, dir: defaultDir(key) }];
  }

  const [primary, secondary] = current;
  if (!primary) return [{ key, dir: defaultDir(key) }];
  if (primary.key === key) return secondary ? [flip(primary), secondary] : [flip(primary)];
  if (secondary?.key === key) return [primary, flip(secondary)];
  return [primary, { key, dir: defaultDir(key) }];
}

/**
 * The Search STA box. A station is written `1+200` on a drawing and stored as
 * `1200`, and people type whichever is in front of them — `parseStation`
 * (`core/alignment.ts`) already accepts both, plus a leading `STA`.
 */
export function parseStationQuery(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  return parseStation(trimmed);
}

/** Half a millimetre, so a station typed at the exact bin edge still matches. */
const EDGE_EPS = 5e-4;

/** True when `display` falls inside the bin's DISPLAY range. Both edges match:
 *  a station on a bin boundary belongs to the two bins that meet there, and
 *  showing both is the honest answer to "what is at 1+220". */
export function stationMatchesBin(bin: StationBin, display: number): boolean {
  const lo = Math.min(bin.staStartDisplayM, bin.staEndDisplayM) - EDGE_EPS;
  const hi = Math.max(bin.staStartDisplayM, bin.staEndDisplayM) + EDGE_EPS;
  return display >= lo && display <= hi;
}

/**
 * Where the scroll container has to be so that one row is fully visible —
 * `scrollIntoView({ block: 'nearest' })`, done as arithmetic because the row
 * may not be rendered yet in a windowed body.
 *
 * The two sticky bands are the whole point of doing this by hand. `scrollTop`
 * 0 is the top of the TABLE, not the top of the first row: the `<thead>` is
 * in flow and covers `headH` px at the top, and the sticky `<tfoot>` covers
 * `footH` px at the bottom. The genuinely visible band is therefore
 * `[current + headH, current + viewport − footH]`; ignoring it parks a row
 * the walk cursor just advanced to a couple of rows below the fold, under the
 * totals.
 *
 * Returns `null` when the row is already fully visible — the caller must not
 * touch `scrollTop`, because scrolling away from where the reader is looking
 * is worse than not scrolling at all.
 */
export interface RevealScrollArgs {
  /** Current `scrollTop` of the container. */
  current: number;
  /** The container's client height. */
  viewport: number;
  /** The row's top, measured in the scrolled content (i.e. `headH + i·rowH`). */
  rowTop: number;
  rowH: number;
  /** Height of the in-flow sticky `<thead>`. */
  headH: number;
  /** Height of the sticky `<tfoot>`. */
  footH: number;
}

export function revealScrollTop({ current, viewport, rowTop, rowH, headH, footH }: RevealScrollArgs): number | null {
  const visibleTop = current + headH;
  const visibleBottom = current + viewport - footH;
  if (rowTop >= visibleTop && rowTop + rowH <= visibleBottom) return null;

  // Above the fold: sit the row immediately under the header. Below it:
  // immediately above the footer. Never negative — there is nothing above
  // the first row to scroll to.
  const next = rowTop < visibleTop ? rowTop - headH : rowTop + rowH - (viewport - footH);
  const clamped = Math.max(0, next);
  return clamped === current ? null : clamped;
}
